import { useState, useEffect, useCallback, useMemo } from "react";
import { API } from "@/App";
import { usePermissions } from "@/hooks/usePermissions";
import axios from "axios";
import { toast } from "sonner";
import {
  Receipt,
  Plus,
  MagnifyingGlass,
  Funnel,
  FilePdf,
  DownloadSimple,
  Trash,
  ClipboardText,
  ArrowRight,
  CheckCircle,
  Car,
  Info,
  PencilSimple,
  EnvelopeSimple
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const TYPE_LABELS = {
  vente_garantie: "Vente avec garantie",
  vente_sans_garantie: "Vente sans garantie",
  achat: "Achat",
  acompte: "Acompte",
  avoir: "Avoir"
};

const STATUS_LABELS = {
  brouillon: "Brouillon",
  envoyee: "Envoyée",
  payee: "Payée",
  partiellement_payee: "Part. payée",
  impayee: "Impayée",
  annulee: "Annulée"
};

const INITIAL_FORM = {
  document_type: "facture",
  type_facture: "vente_garantie",
  type_client: "professionnel",
  mode_paiement_solde: "virement",
  vehicle_id: "",
  client_id: "",
  client_nom: "", client_adresse: "", client_tva: "", client_numero_registre_national: "", client_telephone: "", client_email: "",
  vehicle_description: "", vehicle_vin: "", vehicle_immat: "", vehicle_kilometrage: "", vehicle_annee: "",
  vehicle_cylindree: "", vehicle_puissance: "", vehicle_classe_emission: "", vehicle_date_mec: "",
  documents_remis: "", reprise_description: "", reprise_montant: 0, date_livraison_prevue: "", conditions_particulieres: "",
  montant_ht: 0, acompte_cash: 0, acompte_virement: 0, tva_taux: 21, mentions_legales: ""
};

const DocumentRow = ({ invoice, onStatusChange, onDownloadPdf, onDelete, onConvert, onEdit, onEmail, canUpdate, canDelete, canCreate }) => {
  const isBC = invoice.document_type === "bon_commande";
  return (
    <tr data-testid={`invoice-row-${invoice.invoice_id}`}>
      <td className="font-mono font-medium text-slate-900 text-xs">
        {invoice.numero}
        {invoice.source_bon_commande_id && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">depuis BC</span>}
      </td>
      <td className="text-xs">
        {TYPE_LABELS[invoice.type_facture]}
        {invoice.type_client === "particulier" && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">Particulier</span>}
      </td>
      <td>{invoice.client_nom || "-"}</td>
      <td className="text-xs">{invoice.vehicle_description || "-"}</td>
      <td className="text-right font-semibold text-slate-900">{invoice.montant_ttc?.toLocaleString('fr-BE')} EUR</td>
      <td>
        {isBC && invoice.converted_to_invoice_id ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400" data-testid={`converted-badge-${invoice.invoice_id}`}>
            <CheckCircle size={14} weight="fill" /> Facturé
          </span>
        ) : canUpdate ? (
          <Select value={invoice.statut} onValueChange={(val) => onStatusChange(invoice.invoice_id, val)}>
            <SelectTrigger className="w-32 h-8">
              <span className={`badge badge-${invoice.statut}`}>{STATUS_LABELS[invoice.statut]}</span>
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
            </SelectContent>
          </Select>
        ) : (
          <span className={`badge badge-${invoice.statut}`}>{STATUS_LABELS[invoice.statut]}</span>
        )}
      </td>
      <td className="text-right">
        <div className="flex items-center justify-end gap-1 flex-wrap">
          {isBC && !invoice.converted_to_invoice_id && canCreate && (
            <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
              onClick={() => onConvert(invoice)} data-testid={`convert-bc-${invoice.invoice_id}`}>
              <ArrowRight size={14} className="mr-1" /> Facturer
            </Button>
          )}
          {canUpdate && (
            <Button size="sm" variant="ghost" onClick={() => onEdit(invoice)}
              data-testid={`edit-invoice-${invoice.invoice_id}`} title="Modifier">
              <PencilSimple size={14} />
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onEmail(invoice)}
            data-testid={`email-invoice-${invoice.invoice_id}`} title="Envoyer par e-mail">
            <EnvelopeSimple size={14} />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDownloadPdf(invoice)}
            data-testid={`download-pdf-${invoice.invoice_id}`}>
            <FilePdf size={14} className="mr-1" /> PDF
          </Button>
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  data-testid={`delete-invoice-${invoice.invoice_id}`}>
                  <Trash size={14} />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer {isBC ? "le bon de commande" : "la facture"} {invoice.numero} ?</AlertDialogTitle>
                  <AlertDialogDescription>Cette action est irréversible. Le document et tous les paiements associés seront supprimés.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(invoice.invoice_id)} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </td>
    </tr>
  );
};

const Invoices = () => {
  const { can } = usePermissions();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("factures");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ invoice: null, to: "", subject: "", body: "" });
  const [emailSending, setEmailSending] = useState(false);

  const canCreate = can("invoices", "create");
  const canUpdate = can("invoices", "update");
  const canDelete = can("invoices", "delete");

  const isBC = form.document_type === "bon_commande";

  const fetchData = useCallback(async () => {
    try {
      const [invoicesRes, clientsRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/invoices?limit=200`),
        axios.get(`${API}/clients?limit=200`),
        axios.get(`${API}/vehicles?limit=200`)
      ]);
      setInvoices(invoicesRes.data.data || []);
      setClients(clientsRes.data.data || []);
      setVehicles(vehiclesRes.data.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDialog = (docType) => {
    setForm({ ...INITIAL_FORM, document_type: docType });
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEditDialog = (invoice) => {
    setForm({
      document_type: invoice.document_type || "facture",
      type_facture: invoice.type_facture || "vente_garantie",
      type_client: invoice.type_client || "professionnel",
      mode_paiement_solde: (invoice.mode_paiement_solde || "virement").toLowerCase().includes("cash") ? "cash" : "virement",
      vehicle_id: invoice.vehicle_id || "",
      client_id: invoice.client_id || "",
      client_nom: invoice.client_nom || "",
      client_adresse: invoice.client_adresse || "",
      client_tva: invoice.client_tva || "",
      client_numero_registre_national: invoice.client_numero_registre_national || "",
      client_telephone: invoice.client_telephone || "",
      client_email: invoice.client_email || "",
      vehicle_description: invoice.vehicle_description || "",
      vehicle_vin: invoice.vehicle_vin || "",
      vehicle_immat: invoice.vehicle_immat || "",
      vehicle_kilometrage: invoice.vehicle_kilometrage || "",
      vehicle_annee: invoice.vehicle_annee || "",
      vehicle_cylindree: invoice.vehicle_cylindree || "",
      vehicle_puissance: invoice.vehicle_puissance || "",
      vehicle_classe_emission: invoice.vehicle_classe_emission || "",
      vehicle_date_mec: invoice.vehicle_date_mec || "",
      documents_remis: invoice.documents_remis || "",
      reprise_description: invoice.reprise_description || "",
      reprise_montant: invoice.reprise_montant || 0,
      date_livraison_prevue: invoice.date_livraison_prevue || "",
      conditions_particulieres: invoice.conditions_particulieres || "",
      montant_ht: invoice.montant_ht || 0,
      acompte_cash: invoice.acompte_cash || 0,
      acompte_virement: invoice.acompte_virement || 0,
      tva_taux: invoice.tva_taux ?? 21,
      mentions_legales: invoice.mentions_legales || ""
    });
    setEditingId(invoice.invoice_id);
    setDialogOpen(true);
  };

  const selectedVehicle = useMemo(
    () => vehicles.find(v => v.vehicle_id === form.vehicle_id),
    [vehicles, form.vehicle_id]
  );

  const handleVehicleSelect = (val) => {
    if (val === "none") {
      setForm(f => ({ ...f, vehicle_id: "" }));
      return;
    }
    const v = vehicles.find(x => x.vehicle_id === val);
    setForm(f => ({
      ...f,
      vehicle_id: val,
      vehicle_description: "", vehicle_vin: "", vehicle_immat: "", vehicle_kilometrage: "", vehicle_annee: "",
      vehicle_puissance: v?.puissance_din ? `${v.puissance_din} ch` : f.vehicle_puissance,
      vehicle_date_mec: v?.annee ? String(v.annee) : f.vehicle_date_mec,
      montant_ht: v?.prix_vente || f.montant_ht
    }));
  };

  const handleClientSelect = (val) => {
    setForm(f => ({
      ...f,
      client_id: val === "none" ? "" : val,
      client_nom: "", client_adresse: "", client_tva: "", client_numero_registre_national: ""
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        document_type: form.document_type,
        type_facture: form.type_facture,
        type_client: form.type_client,
        mode_paiement_solde: form.mode_paiement_solde,
        vehicle_id: form.vehicle_id || null,
        client_id: form.client_id || null,
        client_nom: form.client_id ? null : (form.client_nom || null),
        client_adresse: form.client_id ? null : (form.client_adresse || null),
        client_tva: form.client_id ? null : (form.client_tva || null),
        client_numero_registre_national: form.client_id ? null : (form.client_numero_registre_national || null),
        client_telephone: form.client_id ? null : (form.client_telephone || null),
        client_email: form.client_id ? null : (form.client_email || null),
        vehicle_description: form.vehicle_id ? null : (form.vehicle_description || null),
        vehicle_vin: form.vehicle_id ? null : (form.vehicle_vin || null),
        vehicle_immat: form.vehicle_id ? null : (form.vehicle_immat || null),
        vehicle_kilometrage: form.vehicle_id || !form.vehicle_kilometrage ? null : parseInt(form.vehicle_kilometrage),
        vehicle_annee: form.vehicle_id || !form.vehicle_annee ? null : parseInt(form.vehicle_annee),
        vehicle_cylindree: form.vehicle_cylindree || null,
        vehicle_puissance: form.vehicle_puissance || null,
        vehicle_classe_emission: form.vehicle_classe_emission || null,
        vehicle_date_mec: form.vehicle_date_mec || null,
        documents_remis: form.documents_remis || null,
        reprise_description: form.reprise_description || null,
        reprise_montant: form.reprise_montant || 0,
        date_livraison_prevue: form.date_livraison_prevue || null,
        conditions_particulieres: form.conditions_particulieres || null,
        montant_ht: form.montant_ht,
        acompte_cash: form.acompte_cash,
        acompte_virement: form.acompte_virement,
        tva_taux: form.tva_taux,
        mentions_legales: form.mentions_legales || null
      };
      const res = editingId
        ? await axios.put(`${API}/invoices/${editingId}`, payload)
        : await axios.post(`${API}/invoices`, payload);
      toast.success(`${isBC ? "Bon de commande" : "Facture"} ${res.data.numero} ${editingId ? "modifié" : "créé"}${isBC ? "" : "e"} avec succès`);
      setDialogOpen(false);
      setEditingId(null);
      setForm(INITIAL_FORM);
      setActiveTab(isBC ? "bons_commande" : "factures");
      fetchData();
    } catch (error) {
      console.error("Error saving document:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEmail = (invoice) => {
    const docLabel = invoice.document_type === "bon_commande" ? "bon de commande" : "facture";
    const numero = invoice.numero || "";
    const clientNom = invoice.client_nom || "Madame, Monsieur";
    setEmailForm({
      invoice,
      to: invoice.client_email || "",
      subject: `${docLabel === "facture" ? "Facture" : "Bon de commande"} ${numero} - MikAuto Belgium`,
      body: `<p>Bonjour ${clientNom},</p><p>Veuillez trouver en pièce jointe votre ${docLabel} <strong>${numero}</strong>.</p><p>Pour toute question, n'hésitez pas à nous contacter.</p><p>Cordialement,<br/>L'équipe MikAuto Belgium</p>`
    });
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.to || !emailForm.subject) {
      toast.error("Destinataire et sujet requis");
      return;
    }
    setEmailSending(true);
    try {
      await axios.post(`${API}/gmail/send`, {
        to: emailForm.to,
        subject: emailForm.subject,
        body_html: emailForm.body,
        invoice_id: emailForm.invoice.invoice_id,
        client_id: emailForm.invoice.client_id || null,
        vehicle_id: emailForm.invoice.vehicle_id || null
      });
      toast.success("E-mail envoyé avec succès");
      setEmailDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi (Gmail connecté ?)");
    } finally {
      setEmailSending(false);
    }
  };

  const handleConvert = async (invoice) => {
    try {
      const res = await axios.post(`${API}/invoices/${invoice.invoice_id}/convert-to-facture`);
      toast.success(`Bon de commande ${invoice.numero} converti en facture ${res.data.numero}`);
      setActiveTab("factures");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la conversion");
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoice.invoice_id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const prefix = invoice.document_type === "bon_commande" ? "bon_commande" : "facture";
      link.setAttribute('download', `${prefix}_${invoice.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}`, { statut: newStatus });
      toast.success("Statut mis à jour");
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/invoices?format=csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'factures_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleDelete = async (invoiceId) => {
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success("Document supprimé");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const { factures, bonsCommande } = useMemo(() => ({
    factures: invoices.filter(i => i.document_type !== "bon_commande"),
    bonsCommande: invoices.filter(i => i.document_type === "bon_commande")
  }), [invoices]);

  const displayedDocs = useMemo(() => {
    const source = activeTab === "factures" ? factures : bonsCommande;
    return source.filter(inv => {
      const matchSearch = !searchTerm || inv.numero?.toLowerCase().includes(searchTerm.toLowerCase()) || inv.client_nom?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === "all" || inv.statut === statusFilter;
      const matchType = typeFilter === "all" || inv.type_facture === typeFilter;
      return matchSearch && matchStatus && matchType;
    });
  }, [activeTab, factures, bonsCommande, searchTerm, statusFilter, typeFilter]);

  const totalTTC = form.montant_ht * (1 + form.tva_taux / 100);
  const soldeRestant = totalTTC - form.acompte_cash - form.acompte_virement - (isBC ? (form.reprise_montant || 0) : 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-10 w-10"></div>
      </div>
    );
  }

  return (
    <div data-testid="invoices-page" className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Facturation</h1>
          <p className="page-subtitle">{factures.length} facture{factures.length !== 1 ? "s" : ""} · {bonsCommande.length} bon{bonsCommande.length !== 1 ? "s" : ""} de commande</p>
        </div>
        <div className="page-actions">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-invoices-csv">
            <DownloadSimple size={18} className="mr-2" /> Export CSV
          </Button>
          {canCreate && (
            <>
              <Button variant="outline" onClick={() => openDialog("bon_commande")} data-testid="add-bon-commande-btn">
                <ClipboardText size={18} className="mr-2" /> Nouveau bon de commande
              </Button>
              <Button onClick={() => openDialog("facture")} data-testid="add-invoice-btn" className="btn-primary">
                <Plus size={18} className="mr-2" /> Nouvelle facture
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs Factures / Bons de commande */}
      <div className="flex items-center gap-1 mb-6 border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setActiveTab("factures")}
          data-testid="tab-factures"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === "factures" ? "border-blue-600 text-blue-700 dark:text-blue-400" : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}>
          <Receipt size={16} className="inline mr-1.5 -mt-0.5" />
          Factures ({factures.length})
        </button>
        <button
          onClick={() => setActiveTab("bons_commande")}
          data-testid="tab-bons-commande"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === "bons_commande" ? "border-blue-600 text-blue-700 dark:text-blue-400" : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}>
          <ClipboardText size={16} className="inline mr-1.5 -mt-0.5" />
          Bons de commande ({bonsCommande.length})
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="search-invoices" placeholder="Rechercher par numéro ou client..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <div className="flex items-center gap-2">
          <Funnel size={18} className="text-slate-500" />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="filter-type" className="w-48"><SelectValue placeholder="Tous les types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="w-40"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      {displayedDocs.length === 0 ? (
        <div className="empty-state">
          {activeTab === "factures" ? <Receipt size={56} className="empty-state-icon" /> : <ClipboardText size={56} className="empty-state-icon" />}
          <p className="empty-state-title">{activeTab === "factures" ? "Aucune facture trouvée" : "Aucun bon de commande trouvé"}</p>
          <p className="empty-state-description">
            {searchTerm || statusFilter !== "all" || typeFilter !== "all" ? "Modifiez vos critères" : `Commencez par créer ${activeTab === "factures" ? "une facture" : "un bon de commande"}`}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Numéro</th>
                <th>Type</th>
                <th>Client</th>
                <th>Véhicule</th>
                <th className="text-right">Montant TTC</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayedDocs.map((invoice) => (
                <DocumentRow key={invoice.invoice_id} invoice={invoice}
                  onStatusChange={handleUpdateStatus} onDownloadPdf={handleDownloadPdf}
                  onDelete={handleDelete} onConvert={handleConvert}
                  onEdit={openEditDialog} onEmail={handleOpenEmail}
                  canUpdate={canUpdate} canDelete={canDelete} canCreate={canCreate} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-heading-3">{editingId ? `Modifier ${isBC ? "le bon de commande" : "la facture"}` : (isBC ? "Nouveau bon de commande" : "Nouvelle facture")}</DialogTitle>
            <DialogDescription>
              {isBC ? "Créez un bon de commande depuis un véhicule enregistré ou en saisie manuelle." : "Créez une facture depuis un véhicule enregistré ou en saisie manuelle."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            {/* ── Type ── */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Type de vente *</Label>
                <Select value={form.type_facture} onValueChange={(val) => setForm({ ...form, type_facture: val })}>
                  <SelectTrigger data-testid="select-type-facture"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Type de client *</Label>
                <Select value={form.type_client} onValueChange={(val) => {
                  const tva = val === "particulier" ? 0 : 21;
                  const mentions = val === "particulier" ? "Régime particulier — Véhicule d'occasion. Vente soumise au régime de la marge bénéficiaire conformément à l'article 58, §4 du Code de la TVA belge. TVA non applicable." : "";
                  setForm({ ...form, type_client: val, tva_taux: tva, mentions_legales: mentions });
                }}>
                  <SelectTrigger data-testid="select-type-client"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professionnel">Professionnel (TVA 21%)</SelectItem>
                    <SelectItem value="particulier">Particulier (TVA 0%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ── Client ── */}
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 space-y-3">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Client</p>
              <Select value={form.client_id || "none"} onValueChange={handleClientSelect}>
                <SelectTrigger data-testid="select-client"><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Saisie manuelle —</SelectItem>
                  {clients.map(c => (<SelectItem key={c.client_id} value={c.client_id}>{c.prenom} {c.nom}</SelectItem>))}
                </SelectContent>
              </Select>
              {!form.client_id && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="form-label">Nom du client</Label>
                    <Input value={form.client_nom} onChange={(e) => setForm({ ...form, client_nom: e.target.value })} placeholder="Nom complet ou société" data-testid="input-client-nom" />
                  </div>
                  <div>
                    <Label className="form-label">N° TVA (si professionnel)</Label>
                    <Input value={form.client_tva} onChange={(e) => setForm({ ...form, client_tva: e.target.value })} placeholder="BE 0XXX.XXX.XXX" data-testid="input-client-tva" />
                  </div>
                  <div className="col-span-2">
                    <Label className="form-label">Adresse</Label>
                    <Input value={form.client_adresse} onChange={(e) => setForm({ ...form, client_adresse: e.target.value })} placeholder="Rue, numéro, code postal, ville" data-testid="input-client-adresse" />
                  </div>
                  <div className="col-span-2">
                    <Label className="form-label">N° Registre National (RRN)</Label>
                    <Input value={form.client_numero_registre_national} onChange={(e) => setForm({ ...form, client_numero_registre_national: e.target.value })} placeholder="YY.MM.DD-XXX.XX" data-testid="input-client-rrn" />
                  </div>
                  <div>
                    <Label className="form-label">Téléphone</Label>
                    <Input value={form.client_telephone} onChange={(e) => setForm({ ...form, client_telephone: e.target.value })} placeholder="+32 ..." data-testid="input-client-telephone" />
                  </div>
                  <div>
                    <Label className="form-label">E-mail</Label>
                    <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="client@email.be" data-testid="input-client-email" />
                  </div>
                </div>
              )}
            </div>

            {/* ── Véhicule ── */}
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 space-y-3">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Véhicule</p>
              <Select value={form.vehicle_id || "none"} onValueChange={handleVehicleSelect}>
                <SelectTrigger data-testid="select-vehicle"><SelectValue placeholder="Sélectionner un véhicule" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Saisie manuelle —</SelectItem>
                  {vehicles.map(v => (<SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.marque} {v.modele} - {v.reference_interne}</SelectItem>))}
                </SelectContent>
              </Select>
              {form.vehicle_id && selectedVehicle ? (
                <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md text-sm" data-testid="vehicle-prefill-summary">
                  <Car size={20} className="text-blue-600 mt-0.5 shrink-0" />
                  <div className="space-y-0.5 text-slate-700 dark:text-slate-300">
                    <p className="font-semibold">{selectedVehicle.marque} {selectedVehicle.modele} {selectedVehicle.version || ""}</p>
                    <p className="text-xs text-slate-500">
                      {selectedVehicle.vin && <>VIN : {selectedVehicle.vin} · </>}
                      {selectedVehicle.immatriculation && <>Immat : {selectedVehicle.immatriculation} · </>}
                      {selectedVehicle.kilometrage != null && <>{selectedVehicle.kilometrage.toLocaleString('fr-BE')} km · </>}
                      {selectedVehicle.annee && <>{selectedVehicle.annee}</>}
                    </p>
                    {selectedVehicle.prix_vente != null && <p className="text-xs text-blue-700 dark:text-blue-400 font-medium">Prix de vente pré-rempli : {selectedVehicle.prix_vente.toLocaleString('fr-BE')} EUR</p>}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="form-label">Désignation du véhicule</Label>
                    <Input value={form.vehicle_description} onChange={(e) => setForm({ ...form, vehicle_description: e.target.value })} placeholder="Ex : BMW 320d Pack M" data-testid="input-vehicle-description" />
                  </div>
                  <div>
                    <Label className="form-label">N° châssis (VIN)</Label>
                    <Input value={form.vehicle_vin} onChange={(e) => setForm({ ...form, vehicle_vin: e.target.value })} data-testid="input-vehicle-vin" />
                  </div>
                  <div>
                    <Label className="form-label">Immatriculation</Label>
                    <Input value={form.vehicle_immat} onChange={(e) => setForm({ ...form, vehicle_immat: e.target.value })} data-testid="input-vehicle-immat" />
                  </div>
                  <div>
                    <Label className="form-label">Kilométrage</Label>
                    <Input type="number" min="0" value={form.vehicle_kilometrage} onChange={(e) => setForm({ ...form, vehicle_kilometrage: e.target.value })} data-testid="input-vehicle-km" />
                  </div>
                  <div>
                    <Label className="form-label">Année</Label>
                    <Input type="number" min="1950" max="2100" value={form.vehicle_annee} onChange={(e) => setForm({ ...form, vehicle_annee: e.target.value })} data-testid="input-vehicle-annee" />
                  </div>
                </div>
              )}
              {isBC && (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div>
                    <Label className="form-label">Cylindrée</Label>
                    <Input value={form.vehicle_cylindree} onChange={(e) => setForm({ ...form, vehicle_cylindree: e.target.value })} placeholder="Ex : 1968 cm³" data-testid="input-vehicle-cylindree" />
                  </div>
                  <div>
                    <Label className="form-label">Puissance</Label>
                    <Input value={form.vehicle_puissance} onChange={(e) => setForm({ ...form, vehicle_puissance: e.target.value })} placeholder="Ex : 110 kW / 150 ch" data-testid="input-vehicle-puissance" />
                  </div>
                  <div>
                    <Label className="form-label">Classe d&apos;émission</Label>
                    <Input value={form.vehicle_classe_emission} onChange={(e) => setForm({ ...form, vehicle_classe_emission: e.target.value })} placeholder="Ex : Euro 6d" data-testid="input-vehicle-emission" />
                  </div>
                  <div>
                    <Label className="form-label">1ère mise en circulation</Label>
                    <Input value={form.vehicle_date_mec} onChange={(e) => setForm({ ...form, vehicle_date_mec: e.target.value })} placeholder="Ex : 03/2019" data-testid="input-vehicle-mec" />
                  </div>
                  <div className="col-span-2">
                    <Label className="form-label">Documents remis</Label>
                    <Input value={form.documents_remis} onChange={(e) => setForm({ ...form, documents_remis: e.target.value })} placeholder="Certificat d'immatriculation, certificat de conformité, Car-Pass, contrôle technique" data-testid="input-documents-remis" />
                  </div>
                </div>
              )}
            </div>

            {/* ── Montants & Paiement ── */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Montant HT (EUR) *</Label>
                <Input type="number" step="0.01" value={form.montant_ht} onChange={(e) => setForm({ ...form, montant_ht: parseFloat(e.target.value) || 0 })} required data-testid="input-montant-ht" />
              </div>
              <div>
                <Label className="form-label">Taux TVA (%)</Label>
                <Input type="number" step="0.01" value={form.tva_taux} onChange={(e) => setForm({ ...form, tva_taux: parseFloat(e.target.value) || 0 })} data-testid="input-tva" disabled={form.type_client === "particulier"} />
                {form.type_client === "particulier" && <p className="text-xs text-violet-600 mt-1">TVA 0% — Régime de la marge (particulier)</p>}
              </div>
              <div>
                <Label className="form-label">Acompte reçu en cash (EUR)</Label>
                <Input type="number" step="0.01" min="0" value={form.acompte_cash} onChange={(e) => setForm({ ...form, acompte_cash: parseFloat(e.target.value) || 0 })} data-testid="input-acompte-cash" placeholder="0.00" />
              </div>
              <div>
                <Label className="form-label">Acompte reçu par virement (EUR)</Label>
                <Input type="number" step="0.01" min="0" value={form.acompte_virement} onChange={(e) => setForm({ ...form, acompte_virement: parseFloat(e.target.value) || 0 })} data-testid="input-acompte-virement" placeholder="0.00" />
              </div>
              <div className="col-span-2">
                <Label className="form-label">Paiement du solde par *</Label>
                <Select value={form.mode_paiement_solde} onValueChange={(val) => setForm({ ...form, mode_paiement_solde: val })}>
                  <SelectTrigger data-testid="select-mode-paiement-solde"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="virement">Virement bancaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isBC && (
                <>
                  <div>
                    <Label className="form-label">Reprise éventuelle (véhicule)</Label>
                    <Input value={form.reprise_description} onChange={(e) => setForm({ ...form, reprise_description: e.target.value })} placeholder="Ex : VW Golf 7 1.6 TDI — 1-XXX-000" data-testid="input-reprise-description" />
                  </div>
                  <div>
                    <Label className="form-label">Valeur de reprise (EUR)</Label>
                    <Input type="number" step="0.01" min="0" value={form.reprise_montant} onChange={(e) => setForm({ ...form, reprise_montant: parseFloat(e.target.value) || 0 })} data-testid="input-reprise-montant" placeholder="0.00" />
                  </div>
                  <div>
                    <Label className="form-label">Date de livraison prévue</Label>
                    <Input value={form.date_livraison_prevue} onChange={(e) => setForm({ ...form, date_livraison_prevue: e.target.value })} placeholder="Ex : 15/07/2026 ou À convenir" data-testid="input-date-livraison" />
                  </div>
                  <div className="col-span-2">
                    <Label className="form-label">Conditions particulières</Label>
                    <Textarea value={form.conditions_particulieres} onChange={(e) => setForm({ ...form, conditions_particulieres: e.target.value })} rows={2} placeholder="Ex : Véhicule vendu avec 2 jeux de pneus, entretien complet avant livraison..." data-testid="input-conditions-particulieres" />
                  </div>
                </>
              )}
            </div>

            {/* ── Récap ── */}
            {form.montant_ht > 0 && (
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-md text-sm space-y-1" data-testid="invoice-preview-amounts">
                <div className="flex justify-between"><span className="text-slate-500">HT</span><span className="font-medium">{form.montant_ht.toLocaleString('fr-BE', {minimumFractionDigits:2})} EUR</span></div>
                <div className="flex justify-between"><span className="text-slate-500">TVA ({form.tva_taux}%)</span><span className="font-medium">{(form.montant_ht * form.tva_taux / 100).toLocaleString('fr-BE', {minimumFractionDigits:2})} EUR</span></div>
                <div className="flex justify-between border-t pt-1 border-slate-200 dark:border-slate-700"><span className="font-semibold">TTC</span><span className="font-bold text-blue-700 dark:text-blue-400">{totalTTC.toLocaleString('fr-BE', {minimumFractionDigits:2})} EUR</span></div>
                {(form.acompte_cash > 0 || form.acompte_virement > 0 || (isBC && form.reprise_montant > 0)) && (
                  <>
                    {form.acompte_cash > 0 && <div className="flex justify-between text-slate-500"><span>Acompte cash</span><span>- {form.acompte_cash.toLocaleString('fr-BE', {minimumFractionDigits:2})} EUR</span></div>}
                    {form.acompte_virement > 0 && <div className="flex justify-between text-slate-500"><span>Acompte virement</span><span>- {form.acompte_virement.toLocaleString('fr-BE', {minimumFractionDigits:2})} EUR</span></div>}
                    {isBC && form.reprise_montant > 0 && <div className="flex justify-between text-slate-500"><span>Reprise</span><span>- {form.reprise_montant.toLocaleString('fr-BE', {minimumFractionDigits:2})} EUR</span></div>}
                    <div className="flex justify-between border-t pt-1 border-slate-200 dark:border-slate-700">
                      <span className="font-semibold text-red-600">Solde restant ({form.mode_paiement_solde === "cash" ? "cash" : "virement"})</span>
                      <span className="font-bold text-red-600">{soldeRestant.toLocaleString('fr-BE', {minimumFractionDigits:2})} EUR</span>
                    </div>
                  </>
                )}
                {form.type_client === "particulier" && <p className="text-xs text-violet-600 dark:text-violet-400 font-medium pt-1">Régime de la marge — TVA non applicable</p>}
              </div>
            )}

            {/* ── CGV info (bon de commande uniquement) ── */}
            {isBC && (
              <div className="flex items-start gap-2.5 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md text-xs text-amber-800 dark:text-amber-300" data-testid="cgv-info-note">
                <Info size={16} className="mt-0.5 shrink-0" />
                <p>Le bon de commande sera généré en <strong>4 pages</strong> : page 1 bon de commande / facture pro forma avec signatures, page 2 <strong>Conditions Générales de Vente</strong> (modifiables dans <strong>Paramètres → Facturation</strong>), pages 3-4 check-list complète de contrôle du véhicule.</p>
              </div>
            )}

            <div>
              <Label className="form-label">Mentions légales (optionnel)</Label>
              <Textarea value={form.mentions_legales} onChange={(e) => setForm({ ...form, mentions_legales: e.target.value })} rows={2} data-testid="input-mentions-legales" />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingId(null); }}>Annuler</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-invoice-btn" className="btn-primary">
                {submitting
                  ? (editingId ? "Enregistrement..." : "Création...")
                  : editingId
                    ? "Enregistrer les modifications"
                    : (isBC ? "Créer le bon de commande" : "Créer la facture")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Email Dialog ── */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-heading-3">Envoyer par e-mail</DialogTitle>
            <DialogDescription>Le PDF est joint automatiquement. Nécessite Gmail connecté dans Paramètres.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="form-label">Destinataire *</Label>
              <Input type="email" value={emailForm.to} onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })} placeholder="client@email.be" data-testid="email-to" />
            </div>
            <div>
              <Label className="form-label">Sujet *</Label>
              <Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="email-subject" />
            </div>
            <div>
              <Label className="form-label">Message (HTML)</Label>
              <Textarea rows={8} value={emailForm.body} onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} data-testid="email-body" />
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <FilePdf size={14} /> Pièce jointe : {emailForm.invoice?.document_type === "bon_commande" ? "Bon de commande" : "Facture"} {emailForm.invoice?.numero}.pdf
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSendEmail} disabled={emailSending} className="btn-primary" data-testid="send-email-btn">
              <EnvelopeSimple size={16} className="mr-2" />{emailSending ? "Envoi..." : "Envoyer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Share / WhatsApp Dialog (removed) ── */}
    </div>
  );
};

export default Invoices;
